import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  getCartRecoveryCart,
  getCartRecoveryCarts,
  getCartRecoveryFlow,
  getCartRecoveryOverview,
  getCartRecoveryPerformance,
  sendNextCartRecoveryEmail,
  stopCartRecoveryCart,
} from '../../../../gateways/breadstack/cartRecoveryReportApis'
import type {
  CartDetail,
  CartRecoveryRangeParams,
  CartsParams,
} from '../../../../types/cartRecovery'

const ROOT = 'cart-recovery'
const SEND_POLL_INTERVAL_MS = 2500
const SEND_POLL_ATTEMPTS = 8

// The Cart Recovery page and the dashboard widget both read this one query.
export const useCartRecoveryOverviewQuery = (
  storeId: string,
  params: CartRecoveryRangeParams,
  options: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: [ROOT, storeId, 'overview', params] as const,
    queryFn: () => getCartRecoveryOverview(storeId, params),
    enabled: Boolean(storeId) && (options.enabled ?? true),
    placeholderData: keepPreviousData,
  })

export const useCartRecoveryCartsQuery = (
  storeId: string,
  params: CartsParams,
) =>
  useQuery({
    queryKey: [ROOT, storeId, 'carts', params] as const,
    queryFn: () => getCartRecoveryCarts(storeId, params),
    enabled: Boolean(storeId),
    placeholderData: keepPreviousData,
  })

export const useCartRecoveryCartQuery = (
  storeId: string,
  runId: string | null | undefined,
) =>
  useQuery({
    queryKey: [ROOT, storeId, 'cart', runId] as const,
    queryFn: () => getCartRecoveryCart(storeId, runId as string),
    enabled: Boolean(storeId) && Boolean(runId),
    retry: false,
  })

export const useCartRecoveryPerformanceQuery = (
  storeId: string,
  params: CartRecoveryRangeParams,
) =>
  useQuery({
    queryKey: [ROOT, storeId, 'performance', params] as const,
    queryFn: () => getCartRecoveryPerformance(storeId, params),
    enabled: Boolean(storeId),
  })

export const useCartRecoveryFlowQuery = (
  storeId: string,
  params: CartRecoveryRangeParams,
) =>
  useQuery({
    queryKey: [ROOT, storeId, 'flow', params] as const,
    queryFn: () => getCartRecoveryFlow(storeId, params),
    enabled: Boolean(storeId),
  })

export const useInvalidateCartRecovery = (storeId: string) => {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: [ROOT, storeId] })
    queryClient.invalidateQueries({
      queryKey: ['loyalty-flow-customer-detail', storeId],
    })
    queryClient.invalidateQueries({
      queryKey: ['loyalty-flow-customers', storeId],
    })
  }
}

export const useStopCartRecoveryCart = (storeId: string) => {
  const invalidate = useInvalidateCartRecovery(storeId)
  return useMutation({
    mutationFn: (runId: string) => stopCartRecoveryCart(storeId, runId),
    onSuccess: invalidate,
  })
}

export const useSendNextCartRecoveryEmail = (storeId: string) => {
  const invalidate = useInvalidateCartRecovery(storeId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (runId: string) => sendNextCartRecoveryEmail(storeId, runId),
    onSuccess: async ({ cart }, runId) => {
      // The send runs in the background: hold the button off and watch for it,
      // for a bounded time, so the new email row appears on its own.
      const queryKey = [ROOT, storeId, 'cart', runId] as const
      queryClient.setQueryData(queryKey, { ...cart, send_pending: true })
      invalidate()
      for (let i = 0; i < SEND_POLL_ATTEMPTS; i += 1) {
        await new Promise((done) => setTimeout(done, SEND_POLL_INTERVAL_MS))
        const latest = await getCartRecoveryCart(storeId, runId).catch(
          () => null,
        )
        if (!latest) continue
        queryClient.setQueryData(queryKey, {
          ...latest,
          send_pending:
            latest.sent_count > cart.sent_count ? false : latest.send_pending,
        })
        if (latest.sent_count > cart.sent_count || !latest.send_pending) break
      }
      // Gave up waiting: don't leave the button off.
      queryClient.setQueryData(
        queryKey,
        (current: CartDetail | undefined) =>
          current && { ...current, send_pending: false },
      )
      invalidate()
    },
  })
}
